import { QueryClient } from '@tanstack/react-query'

import {
    COP_API_BASE_URL,
    PRODUCT_CATEGORIES_ENDPOINT,
    ROLES_ENDPOINT,
    USERS_ENDPOINT,
} from '../../../constants/service-endpoints'
import { get } from '../../../services/http.service'
import { fetchCustomersData } from '../../../services/customerSearch.service'
import type { User } from '../../../services/usersManagement'

export interface Customer {
    id: string
    name: string
}

export interface Role {
    id: string | number
    name: string
    description?: string
}

export interface ProductCategory {
    id: string | number
    name: string
    businessUnitShortName?: string
}

const userManagementQueryClient = new QueryClient()

export async function fetchCustomers(searchTerm?: string, skip = 0): Promise<Customer[]> {
    const data = await fetchCustomersData<Customer>({
        cacheKeyPrefix: 'um:customers',
        searchTerm,
        skip,
        top: 50,
    })
    return data.value ?? []
}

export async function fetchRoles(): Promise<Role[]> {
    return userManagementQueryClient.fetchQuery({
        queryKey: ['um:roles'],
        staleTime: Infinity,
        queryFn: async () => {
            const data = await get<Role[]>(`${COP_API_BASE_URL}${ROLES_ENDPOINT}`)
            return Array.isArray(data) ? data : []
        },
    })
}

export async function fetchProductCategories(): Promise<ProductCategory[]> {
    return userManagementQueryClient.fetchQuery({
        queryKey: ['um:productCategories'],
        staleTime: Infinity,
        queryFn: async () => {
            const url = `${COP_API_BASE_URL}${PRODUCT_CATEGORIES_ENDPOINT}?$orderby=businessUnitShortName, name`
            const data = await get<{ value: ProductCategory[] } | ProductCategory[]>(url)
            return Array.isArray(data) ? data : ((data as { value: ProductCategory[] }).value ?? [])
        },
    })
}

export async function fetchAllUsers(): Promise<User[]> {
    return userManagementQueryClient.fetchQuery({
        queryKey: ['um:allUsers'],
        staleTime: Infinity,
        queryFn: async () => {
            const data = await get<User[] | { value: User[] }>(`${COP_API_BASE_URL}${USERS_ENDPOINT}`)
            return Array.isArray(data) ? data : (data.value ?? [])
        },
    })
}
